/**
 * Sub-processor change-event + acknowledgement client.
 *
 *   GET    /api/admin/subprocessor-changes
 *   POST   /api/admin/subprocessor-changes
 *   GET    /api/organizations/{id}/subprocessor-changes/pending
 *   POST   /api/organizations/{id}/subprocessor-changes/{change_id}/acknowledge
 *
 * The pending list is read by the in-app SubProcessorChangeBanner; the
 * acknowledge POST is fired from the banner's confirm button by an
 * ORG_OWNER.
 */

import { fetchWithAuth } from '@/lib/utils/api-auth';
import { extractApiError, type ApiErrorParams } from '@/lib/api/shared';

export type SubprocessorChangeKind = 'added' | 'removed' | 'role_changed' | 'region_changed';

export interface SubprocessorChangeEvent {
  id: string;
  subprocessorId: string;
  changeKind: SubprocessorChangeKind;
  details: Record<string, unknown>;
  effectiveAt: string;
  createdAt: string;
}

export class ApiError extends Error {
  readonly code: string;
  readonly params?: ApiErrorParams;
  readonly status: number;

  constructor(code: string, message: string, status: number, params?: ApiErrorParams) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.params = params;
  }
}

async function throwApiError(res: Response, fallback: string): Promise<never> {
  const err = await extractApiError(res);
  throw new ApiError(err.code || fallback, err.message ?? err.code ?? fallback, err.status, err.params);
}

export async function getPendingSubprocessorChanges(orgId: string): Promise<SubprocessorChangeEvent[]> {
  const res = await fetchWithAuth(`/api/organizations/${orgId}/subprocessor-changes/pending`);
  if (!res.ok) await throwApiError(res, 'subproc_pending_failed');
  const json = await res.json();
  return (json.data as SubprocessorChangeEvent[]) ?? [];
}

export async function acknowledgeSubprocessorChange(orgId: string, changeId: string): Promise<void> {
  const res = await fetchWithAuth(`/api/organizations/${orgId}/subprocessor-changes/${changeId}/acknowledge`, {
    method: 'POST',
  });
  if (!res.ok) await throwApiError(res, 'subproc_ack_failed');
}
